import React, { useState, useEffect } from 'react'
import { useNotificationContext } from '../../contexts/NotificationContext'

interface Client { id: number; name: string }

/**
 * Obiekt infrastruktury z listy wyboru (`GET /api/infrastructure/client/{id}`).
 *
 * Endpoint NIE zwraca współrzędnych — wybór obiektu uzupełnia miejscowość i adres,
 * ale nie dotyka GPS.
 */
interface InfrastructureOption {
  id: number
  name: string
  address?: string | null
  location?: string | null
  street_address?: string | null
}

interface ServiceData {
  client_id: number
  service_type: 'odbiór_przyłącza' | 'częściowy_odbiór_przyłącza' | 'wymiana_wodomierza' | 'usunięcie_awarii' | 'zamknięcie_przyłącza' | 'kontrola_stacji_uzdatniania_wody' | 'zlecenie_wewnętrzne' | 'inne'
  location: string
  street_address: string
  latitude: string
  longitude: string
  description: string
  contact_person_name: string
  contact_phone: string
  additional_tools: string
  priority: 'niski' | 'średni' | 'wysoki'
  related_issue_id?: string
  planned_date?: string
  infrastructure_object_id: number | null
}

interface ServiceCreatorProps {
  isOpen: boolean
  onClose: () => void
  onServiceCreated: () => void
  token: string
  editingServiceId?: string | null
}

export default function ServiceCreator({ isOpen, onClose, onServiceCreated, token, editingServiceId = null }: ServiceCreatorProps) {
  const [clients, setClients] = useState<Client[]>([])
  const [issues, setIssues] = useState<Array<{id: string, reporter_name: string, street_address: string}>>([])
  const [loading, setLoading] = useState(false)
  const [issueData, setIssueData] = useState<any>(null)
  const [infrastructureObjects, setInfrastructureObjects] = useState<InfrastructureOption[]>([])
  const [loadingInfrastructure, setLoadingInfrastructure] = useState(false)
  // Flaga wyłącznie kliencka — backend przyjmuje samo `infrastructure_object_id`.
  const [relatesToInfrastructure, setRelatesToInfrastructure] = useState(false)
  const { showSuccess, showError } = useNotificationContext()

  const [form, setForm] = useState<ServiceData>({
    client_id: 0,
    service_type: 'wymiana_wodomierza',
    location: '',
    street_address: '',
    latitude: '',
    longitude: '',
    description: '',
    contact_person_name: '',
    contact_phone: '',
    additional_tools: '',
    priority: 'średni',
    infrastructure_object_id: null,
  })

  useEffect(() => {
    if (isOpen) {
      const urlParams = new URLSearchParams(window.location.search)
      const createFromIssue = urlParams.get('createFromIssue')

      loadClients()

      if (editingServiceId) {
        // Editing mode - load existing service data
        loadServiceDataForEdit(editingServiceId)
      } else if (createFromIssue) {
        loadIssueData(createFromIssue)
      }
    }
  }, [isOpen, editingServiceId])

  useEffect(() => {
    if (form.client_id && !issueData) {
      loadIssuesForClient(form.client_id)
    }
  }, [form.client_id, issueData])

  const loadIssueData = async (issueId: string) => {
    try {
      const response = await fetch(`/api/issues/${issueId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setIssueData(data)

        // Pre-fill form with issue data
        setForm({
          client_id: data.client_id,
          service_type: 'wymiana_wodomierza', // Default service type
          location: data.location || '',
          street_address: data.street_address || '',
          latitude: data.latitude || '',
          longitude: data.longitude || '',
          description: data.additional_info || '',
          contact_person_name: data.reporter_name || '',
          contact_phone: data.reporter_phone || '',
          additional_tools: '',
          priority: 'średni',
          related_issue_id: data.id,
          infrastructure_object_id: null,
        })
      }
    } catch (error) {
      console.error('Error loading issue data:', error)
    }
  }

  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
        if (data.length > 0 && !form.client_id) {
          setForm(f => ({ ...f, client_id: data[0].id }))
        }
      }
    } catch (error) {
      console.error('Error loading clients:', error)
    }
  }

  const loadServiceDataForEdit = async (serviceId: string) => {
    try {
      const response = await fetch(`/api/services/${serviceId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()

        // Fill form with existing service data
        setForm({
          client_id: data.client_id,
          service_type: data.service_type,
          location: data.location || '',
          street_address: data.street_address,
          latitude: data.latitude || '',
          longitude: data.longitude || '',
          description: data.description || '',
          contact_person_name: data.contact_person_name || '',
          contact_phone: data.contact_phone || '',
          additional_tools: data.additional_tools || '',
          priority: data.priority,
          related_issue_id: data.related_issue_id || undefined,
          infrastructure_object_id: data.infrastructure_object_id ?? null,
        })

        // Powiązanie z obiektem ma przetrwać edycję — bez tego zapis by je zerwał.
        if (data.infrastructure_object_id) {
          setRelatesToInfrastructure(true)
          loadInfrastructureObjects(data.client_id)
        }

        // Load related issues for this client
        if (data.client_id) {
          loadIssuesForClient(data.client_id)
        }
      } else {
        showError('Błąd', 'Nie udało się załadować danych zlecenia')
        onClose()
      }
    } catch (error) {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
      onClose()
    }
  }

  /**
   * Obiekty WYBRANEGO referatu — backend odrzuca obiekt spoza referatu zlecenia (404),
   * więc lista musi iść za wyborem klienta.
   */
  const loadInfrastructureObjects = async (clientId: number) => {
    try {
      setLoadingInfrastructure(true)
      const response = await fetch(`/api/infrastructure/client/${clientId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (!response.ok) throw new Error()
      const data: InfrastructureOption[] = await response.json()
      setInfrastructureObjects(data)
    } catch {
      showError('Błąd ładowania', 'Nie można pobrać listy obiektów infrastruktury')
      setInfrastructureObjects([])
    } finally {
      setLoadingInfrastructure(false)
    }
  }

  const loadIssuesForClient = async (clientId: number) => {
    try {
      const response = await fetch(`/api/issues?client_id=${clientId}&status=zaadresowane,w_trakcie_realizacji,zrealizowane`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setIssues(data)
      }
    } catch (error) {
      console.error('Error loading issues:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!form.client_id || !form.street_address.trim()) {
      showError('Błąd walidacji', 'Klient i adres są wymagane')
      return
    }

    if (relatesToInfrastructure && !form.infrastructure_object_id) {
      showError('Błąd walidacji', 'Wybierz obiekt infrastruktury')
      return
    }

    setLoading(true)
    try {
      const isEditing = editingServiceId !== null
      const url = isEditing ? `/api/services/${editingServiceId}` : '/api/services'
      const method = isEditing ? 'PUT' : 'POST'

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          related_issue_id: form.related_issue_id || null,
          // „Nie" wysyła null — to świadome odpięcie obiektu od zlecenia.
          infrastructure_object_id: relatesToInfrastructure ? form.infrastructure_object_id : null
        }),
      })

      if (response.ok) {
        const data = await response.json()
        showSuccess('Sukces', data.message)
        handleClose()
        onServiceCreated()
      } else {
        const errorData = await response.json()
        showError('Błąd', errorData.detail || `Nie udało się ${isEditing ? 'zaktualizować' : 'utworzyć'} serwisu`)
      }
    } catch (error) {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setForm({
      client_id: 0,
      service_type: 'wymiana_wodomierza',
      location: '',
      street_address: '',
      latitude: '',
      longitude: '',
      description: '',
      contact_person_name: '',
      contact_phone: '',
      additional_tools: '',
      priority: 'średni',
      infrastructure_object_id: null,
    })
    setIssues([])
    setInfrastructureObjects([])
    setRelatesToInfrastructure(false)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch justify-center z-50 sm:items-center sm:p-4">
      <div className="bg-white sm:rounded-lg shadow-xl w-full sm:max-w-2xl max-h-full sm:max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b shrink-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
            {editingServiceId ? 'Edytuj zlecenie' : 'Nowe zlecenie'}
          </h2>
          <button onClick={handleClose} className="p-1 -mr-1 text-gray-400 hover:text-gray-600" aria-label="Zamknij">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-6">
            {/* Klient */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Klient *</label>
              <select
                value={form.client_id}
                onChange={(e) => {
                  const clientId = parseInt(e.target.value)
                  // Obiekty należą do referatu — zmiana klienta unieważnia wybór.
                  setForm({ ...form, client_id: clientId, infrastructure_object_id: null })
                  setInfrastructureObjects([])
                  if (relatesToInfrastructure && clientId) {
                    loadInfrastructureObjects(clientId)
                  }
                }}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">— wybierz klienta —</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            {/* Powiązane zgłoszenie */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Powiązane zgłoszenie (opcjonalne)</label>
              {issueData ? (
                <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700">
                  {issueData.id} - {issueData.reporter_name} ({issueData.street_address})
                </div>
              ) : (
                <select
                  value={form.related_issue_id || ''}
                  onChange={(e) => setForm({ ...form, related_issue_id: e.target.value || undefined })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— brak powiązania —</option>
                  {issues.map(issue => (
                    <option key={issue.id} value={issue.id}>
                      {issue.id} - {issue.reporter_name} ({issue.street_address})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Obiekt infrastruktury */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dotyczy obiektu infrastruktury?</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setRelatesToInfrastructure(false)
                    setForm({ ...form, infrastructure_object_id: null })
                  }}
                  className={`px-4 py-2 rounded-md border text-sm ${!relatesToInfrastructure ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
                >
                  Nie
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRelatesToInfrastructure(true)
                    if (form.client_id) {
                      loadInfrastructureObjects(form.client_id)
                    }
                  }}
                  className={`px-4 py-2 rounded-md border text-sm ${relatesToInfrastructure ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}`}
                >
                  Tak
                </button>
              </div>

              {relatesToInfrastructure && (
                <div className="mt-4">
                  {loadingInfrastructure ? (
                    <div className="text-sm text-gray-600">Ładowanie obiektów infrastruktury...</div>
                  ) : infrastructureObjects.length === 0 ? (
                    <div className="text-sm text-gray-600">Brak obiektów infrastruktury dla wybranego klienta.</div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Wybierz obiekt infrastruktury *</label>
                      <select
                        value={form.infrastructure_object_id ?? ''}
                        onChange={(e) => {
                          const selectedId = e.target.value ? parseInt(e.target.value, 10) : null
                          const selectedObj = infrastructureObjects.find(obj => obj.id === selectedId)
                          if (!selectedObj) {
                            setForm({ ...form, infrastructure_object_id: null })
                            return
                          }
                          // Uzupełniamy adres, ale NIE ruszamy GPS: endpoint listy nie
                          // zwraca współrzędnych, więc zerowanie ich gubiłoby pozycję.
                          setForm({
                            ...form,
                            infrastructure_object_id: selectedId,
                            location: selectedObj.location || form.location,
                            street_address: selectedObj.street_address || form.street_address,
                          })
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">— wybierz obiekt —</option>
                        {infrastructureObjects.map(obj => (
                          <option key={obj.id} value={obj.id}>
                            {[obj.name, obj.location, obj.street_address].filter(Boolean).join(' – ')}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Typ serwisu */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rodzaj prac *</label>
              <select
                value={form.service_type}
                onChange={(e) => setForm({ ...form, service_type: e.target.value as any })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
              <option value="odbiór_przyłącza">Odbiór przyłącza</option>
              <option value="częściowy_odbiór_przyłącza">Częściowy odbiór przyłącza</option>
              <option value="wymiana_wodomierza">Wymiana wodomierza</option>
              <option value="usunięcie_awarii">Usunięcie awarii</option>
              <option value="zamknięcie_przyłącza">Zamknięcie przyłącza</option>
              <option value="kontrola_stacji_uzdatniania_wody">Kontrola stacji uzdatniania wody</option>
              <option value="zlecenie_wewnętrzne">Zlecenie wewnętrzne (urząd)</option>
              <option value="skoordynacja">Koordynacja</option>
              <option value="inne">Inne</option>
              </select>
            </div>

            {/* Planowana data - tylko w edycji */}
            {editingServiceId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Planowana data wykonania</label>
                <input
                  type="date"
                  value={form.planned_date || ''}
                  onChange={(e) => setForm({ ...form, planned_date: e.target.value ? e.target.value : undefined })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* Priorytet */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">Priorytet</label>
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
                {[
                  { value: 'niski', label: 'Niski (do zaplanowania w ciągu 14 dni)', color: 'text-green-600' },
                  { value: 'średni', label: 'Średni (do zaplanowania w ciągu 7 dni)', color: 'text-yellow-600' },
                  { value: 'wysoki', label: 'Wysoki (do zaplanowania na następny dzień)', color: 'text-red-600' }
                ].map(priority => (
                  <label key={priority.value} className="flex items-center min-h-[44px] sm:min-h-0">
                    <input
                      type="radio"
                      name="priority"
                      value={priority.value}
                      checked={form.priority === priority.value}
                      onChange={(e) => setForm({ ...form, priority: e.target.value as any })}
                      className="mr-2"
                    />
                    <span className={`text-sm ${priority.color}`}>{priority.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Lokalizacja */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Miejscowość</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Warszawa"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ulica/nr działki *</label>
                <input
                  type="text"
                  value={form.street_address}
                  onChange={(e) => setForm({ ...form, street_address: e.target.value })}
                  placeholder="ul. Przykładowa 123"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Szerokość geograficzna</label>
                <input
                  type="text"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  placeholder="52.2297"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Długość geograficzna</label>
                <input
                  type="text"
                  value={form.longitude}
                  onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                  placeholder="21.0122"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Opis prac */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dodatkowe informacje / opis prac do wykonania</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                placeholder="Opisz szczegółowo jakie prace mają być wykonane..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* Osoba kontaktowa (opcjonalnie) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Osoba kontaktowa (imię i nazwisko)</label>
                <input
                  type="text"
                  value={form.contact_person_name}
                  onChange={(e) => setForm({ ...form, contact_person_name: e.target.value })}
                  placeholder="np. Jan Kowalski"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Telefon kontaktowy</label>
                <input
                  type="text"
                  value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="np. +48 600 000 000"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Dodatkowe narzędzia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Potrzebne usługi/narzędzia dodatkowe</label>
              <textarea
                value={form.additional_tools}
                onChange={(e) => setForm({ ...form, additional_tools: e.target.value })}
                rows={2}
                placeholder="Lista wymaganych narzędzi, materiałów lub usług..."
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </div>
          </div>

          <div className="shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 p-4 sm:p-6 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={loading}
              className="min-h-[44px] px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
            >
              {loading ? (editingServiceId ? 'Zapisywanie...' : 'Tworzenie...') : (editingServiceId ? 'Zapisz zmiany' : 'Utwórz zlecenie')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
